import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useClientAuth } from '../../context/ClientAuthContext';
import clientAuthService from '../../services/clientAuthService';
import AuthBackgroundLayout from '../../components/AuthBackgroundLayout';

const ClientLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientLogin } = useClientAuth();
  const [showLoginCard, setShowLoginCard] = useState(!!location.state?.showLogin);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotIsError, setForgotIsError] = useState(false);

  // Consume the one-time "arrived from register/verify" flag so a later
  // page refresh on this same URL falls back to the landing icon, not the form.
  useEffect(() => {
    if (location.state?.showLogin) {
      navigate('.', { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    if (error) setError('');
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    setForgotMessage('');
    setForgotIsError(false);
    try {
      const data = await clientAuthService.forgotPassword(forgotEmail);
      setForgotMessage(data.message || 'Your password has been sent to your email.');
      setForgotIsError(false);
    } catch (err) {
      setForgotMessage(err.error || 'Failed to process request');
      setForgotIsError(true);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      const data = await clientAuthService.login(formData.email, formData.password);
      clientLogin(data.access_token, data.refresh_token, data.user);
      navigate('/client/dashboard');
    } catch (err) {
      setError(err.error || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  if (!showLoginCard) {
    return (
      <AuthBackgroundLayout>
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <button
            type="button"
            onClick={() => setShowLoginCard(true)}
            className="client-landing-icon-btn"
            style={{
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              border: 'none',
              background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)',
              color: '#fff',
              boxShadow: '0 8px 30px rgba(13,110,253,0.55)',
              fontSize: '3rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              animation: 'client-landing-pulse 2.5s ease-in-out infinite',
            }}
            aria-label="Client Login"
          >
            <i className="fas fa-sign-in-alt"></i>
          </button>
          <h4
            className="mt-4 fw-bold"
            style={{ color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}
          >
            Client Login
          </h4>
          <p style={{ color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>
            Click above to access your certificate portal
          </p>
          <style>{`
            @keyframes client-landing-pulse {
              0% { box-shadow: 0 8px 30px rgba(13,110,253,0.55), 0 0 0 0 rgba(13,110,253,0.5); }
              70% { box-shadow: 0 8px 30px rgba(13,110,253,0.55), 0 0 0 20px rgba(13,110,253,0); }
              100% { box-shadow: 0 8px 30px rgba(13,110,253,0.55), 0 0 0 0 rgba(13,110,253,0); }
            }
            .client-landing-icon-btn:hover {
              transform: scale(1.06);
              transition: transform 0.2s ease;
            }
          `}</style>
        </div>
      </AuthBackgroundLayout>
    );
  }

  return (
    <AuthBackgroundLayout>
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8} lg={5}>
          <div className="text-end mb-2">
            <Button
              variant="light"
              size="sm"
              className="shadow-sm"
              onClick={() => setShowLoginCard(false)}
            >
              <i className="fas fa-home me-1"></i>Home
            </Button>
          </div>
          <Card className="shadow-lg">
            <Card.Header className="text-white text-center py-3"
              style={{ background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)' }}>
              <h3 className="mb-0">
                <i className="fas fa-sign-in-alt me-2"></i>
                Client Login
              </h3>
              <small>Equipment Inspection Certificate Portal</small>
            </Card.Header>

            <Card.Body className="p-4">
              {showForgotPassword ? (
                <>
                  <h5 className="mb-3">
                    <i className="fas fa-key me-2"></i>
                    Forgot Password
                  </h5>
                  {forgotMessage && (
                    <Alert variant={forgotIsError ? 'danger' : 'success'} dismissible onClose={() => setForgotMessage('')}>
                      {forgotMessage}
                    </Alert>
                  )}
                  <Form onSubmit={handleForgotPassword}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <i className="fas fa-envelope me-2"></i>
                        Email Address <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Enter your registered email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                      />
                      <Form.Text className="text-muted">
                        If this email is registered, your existing password will be emailed to it.
                      </Form.Text>
                    </Form.Group>
                    <div className="d-grid gap-2">
                      <Button variant="primary" type="submit" disabled={forgotLoading}>
                        {forgotLoading
                          ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending...</>
                          : <><i className="fas fa-paper-plane me-2"></i>Send Password</>}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={() => { setShowForgotPassword(false); setForgotMessage(''); setForgotEmail(''); }}
                      >
                        <i className="fas fa-arrow-left me-2"></i>Back to Login
                      </Button>
                    </div>
                  </Form>
                </>
              ) : (
              <>
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  <i className="fas fa-exclamation-circle me-2"></i>{error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="fas fa-envelope me-2"></i>
                    Email Address <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="fas fa-lock me-2"></i>
                    Password <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                    <Button
                      variant="link"
                      className="position-absolute end-0 top-0 text-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ textDecoration: 'none' }}
                      type="button"
                    >
                      <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </Button>
                  </div>
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  className="w-100 py-2 fw-bold"
                  disabled={loading}
                >
                  {loading ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>Logging in...</>
                  ) : (
                    <><i className="fas fa-sign-in-alt me-2"></i>Login</>
                  )}
                </Button>
              </Form>

              <div className="text-center mt-3">
                <small className="text-muted">
                  Forgot your password?{' '}
                  <a href="#" className="text-primary" onClick={(e) => { e.preventDefault(); setShowForgotPassword(true); }}>
                    Reset it here
                  </a>
                </small>
              </div>
              </>
              )}
            </Card.Body>

            <Card.Footer className="text-center py-3">
              <small>
                Don't have an account?{' '}
                <a href="/client/register" className="text-primary fw-bold">Register here</a>
              </small>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
    </AuthBackgroundLayout>
  );
};

export default ClientLogin;
